<script setup lang="ts">
    import { computed, shallowReactive, onMounted, watch, ref } from 'vue'
    import { useRoute } from 'vue-router'
    import moment from 'moment'

    import { generateName } from '@common/Common'
    import { guid } from '@common/Guid'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import Navigation from '@common/Navigation.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'
    import ProjectPlanEditor from '@Quest/ProjectPlans/Components/ProjectPlanEditor.vue'

    import { useManageProjectPlansPageQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'

    import
    {
        useCreateProjectPlanCommandV1,
        useCloneProjectPlanCommandV1,
        useDeleteProjectPlanCommandV1,
        useExportProjectPlanCommandV1,
        useSubmitProjectPlanCommandV1,
        useUnsubmitProjectPlanCommandV1,
        useUpdateProjectPlanCommandV1,
        useCreatePlanFromTemplateCommandV1
    } from '@Quest/ProjectPlans/commands-vue-composables'

    import { useUpdateProjectActivePlanCommandV1 } from '@Quest/Projects/commands-vue-composables'
    import { useCreateEnterpriseTemplateFromProjectPlanCommandV1 } from '@Quest/ProjectPlanTemplates/commands-vue-composables'

    import type { ManageProjectPlansPageReadModel, ProjectPlanReadModel } from '@Quest/ProjectPlans/readmodels'
    import type { ProjectPlanTemplateReadModel } from '@Quest/ProjectPlanTemplates/readmodels'
    import { ProjectPlanEditorFormSubmitEventPayload, ProjectPlanEditorViewModel } from '@Quest/ProjectPlans/Components/ProjectPlanEditor'

    const route = useRoute()
    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))

    const projectPlanEditor = shallowReactive(new ProjectPlanEditorViewModel())

    const rm = ref<ManageProjectPlansPageReadModel | null>(null)
    const projectName = computed(() => rm.value?.ProjectName ?? '')
    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')

    const userId = computed(() => rm.value?.UserId)
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const isProjectOwner = computed(() => rm.value?.IsProjectOwner ?? false)
    const isStrategyAdmin = computed(() => rm.value?.IsStrategyAdmin ?? false)

    const activePlan = computed<ProjectPlanReadModel | null>(() => rm.value?.Plans.find(p => p.IsActive) ?? null)
    const backupPlans = computed<ProjectPlanReadModel[]>(() => rm.value?.Plans.filter(p => !p.IsActive) ?? [])
    const planTemplates = computed<ProjectPlanTemplateReadModel[]>(() => rm.value?.PlanTemplates ?? [])

    const {
        runManageProjectPlansPageQueryV1,
        modelManageProjectPlansPageQueryV1,
    } = useManageProjectPlansPageQueryV1(workspaceId, tchProjectId, projectId)
   

    /* ---------------------------------- */
    /* Formatting */
    /* ---------------------------------- */

    function getFormattedDateStr(date: string | null)
    {
        if (!date) return null
        return moment(date).format('MMM D, YYYY')
    }

    function getFormattedTimeStr(date: string | null)
    {
        if (!date) return null
        return moment(date).format('LT')
    }

    /* ---------------------------------- */
    /* Editor actions */
    /* ---------------------------------- */

    function showPlanCreator(template: ProjectPlanTemplateReadModel | null = null)
    {
        projectPlanEditor.showCreator(template)
    }

    function showPlanEditor(plan: ProjectPlanReadModel)
    {
        projectPlanEditor.showEditor(plan)
    }

    /* ---------------------------------- */
    /* Commands */
    /* ---------------------------------- */

    const { invokeCreateProjectPlanCommandV1, onSucceedCreateProjectPlanCommandV1 } =
        useCreateProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeUpdateProjectPlanCommandV1, onSucceedUpdateProjectPlanCommandV1 } =
        useUpdateProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeCloneProjectPlanCommandV1, onSucceedCloneProjectPlanCommandV1 } =
        useCloneProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeDeleteProjectPlanCommandV1, onSucceedDeleteProjectPlanCommandV1 } =
        useDeleteProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeSubmitProjectPlanCommandV1, onSucceedSubmitProjectPlanCommandV1 } =
        useSubmitProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeUnsubmitProjectPlanCommandV1, onSucceedUnsubmitProjectPlanCommandV1 } =
        useUnsubmitProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeUpdateProjectActivePlanCommandV1, onSucceedUpdateProjectActivePlanCommandV1 } =
        useUpdateProjectActivePlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeCreateEnterpriseTemplateFromProjectPlanCommandV1, onSucceedCreateEnterpriseTemplateFromProjectPlanCommandV1 } =
        useCreateEnterpriseTemplateFromProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    const { invokeCreatePlanFromTemplateCommandV1, onSucceedCreatePlanFromTemplateCommandV1 } =
        useCreatePlanFromTemplateCommandV1(userId, workspaceId, portfolioId, projectId)

    const {
        invokeExportProjectPlanCommandV1,
        onSucceedExportProjectPlanCommandV1,
        result: resultExportProjectPlanCommandV1
    } = useExportProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId)

    /* ---------------------------------- */
    /* Command handlers */
    /* ---------------------------------- */

    function handleProjectPlanEditorFormSubmit(e: ProjectPlanEditorFormSubmitEventPayload)
    {
        if (!rm.value) return

        if (e.id == null)
        {
            invokeCreateProjectPlanCommandV1({
                Id: guid(),
                PlanName: e.planName,
                DurationInMonths: e.durationInMonths,
                Granularity: e.granularity,
                Denomination: e.denomination,
                SubmissionDueDateIsoString: e.submissionDueDateIsoString
            })
        }
        else
        {
            invokeUpdateProjectPlanCommandV1({
                Id: e.id,
                PlanName: e.planName,
                DurationInMonths: e.durationInMonths,
                Granularity: e.granularity,
                Denomination: e.denomination,
                SubmissionDueDateIsoString: e.submissionDueDateIsoString
            })
        }
    }

    function handleSubmitProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        if (confirm(`Are you sure you want to submit the plan: ${plan.Name}`))
        {
            invokeSubmitProjectPlanCommandV1({ Id: plan.Id })
        }
    }

    function handleUnsubmitProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        if (confirm(`Are you sure you want unsubmit the plan: ${plan.Name}`))
        {
            invokeUnsubmitProjectPlanCommandV1({ Id: plan.Id })
        }
    }

    function handleDeleteProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        if (confirm(`Are you sure you want to delete: ${plan.Name}`))
        {
            invokeDeleteProjectPlanCommandV1({
                Id: plan.Id,
            })
        }
    }

    function handleCloneProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        invokeCloneProjectPlanCommandV1({
            Id: guid(),
            SourcePlanId: plan.Id,
            ClonePlanName: generateName(plan.Name)
        })
    }

    function handleUpdateProjectActivePlanCommand(plan: ProjectPlanReadModel)
    {
        invokeUpdateProjectActivePlanCommandV1({
            ProjectPlanId: plan.Id
        })
    }

    function handleExportProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        invokeExportProjectPlanCommandV1({
            Id: plan.Id,
        })
    }

    function handleCreateEnterpriseTemplateFromProjectPlanCommand(plan: ProjectPlanReadModel)
    {
        if (!isStrategyAdmin.value) return

        invokeCreateEnterpriseTemplateFromProjectPlanCommandV1({
            ProjectPlanId: plan.Id,
            Id: guid(),
            StrategyId: rm.value?.StrategyId,
            Name: generateName("Template"),
            Description: `Template created from project plan ${plan.Name}`
        })
    }

    function handleCreatePlanFromTemplateCommand(template: ProjectPlanTemplateReadModel)
    {
        invokeCreatePlanFromTemplateCommandV1({
            Id: guid(),
            ProjectPlanTemplateId: template.Id,
            PlanName: generateName(template.Name)
        })
    }

    onMounted(() =>
    {
        document.title = 'Project | TransparentChoice'
        runManageProjectPlansPageQueryV1()
    })

    watch(modelManageProjectPlansPageQueryV1, (current) =>
    {
        if (current?.RedirectUrl)
        {
            window.location.replace(current.RedirectUrl)
        }

        if (current)
        {
            rm.value = current
        }
    })


    onSucceedCreateProjectPlanCommandV1(() =>
    {
        projectPlanEditor.close()
        runManageProjectPlansPageQueryV1()
    })

    onSucceedUpdateProjectPlanCommandV1(() =>
    {
        projectPlanEditor.close()
        runManageProjectPlansPageQueryV1()
    })

    onSucceedCloneProjectPlanCommandV1(() =>
    {
        runManageProjectPlansPageQueryV1()
    })

    onSucceedDeleteProjectPlanCommandV1(() =>
    {
        runManageProjectPlansPageQueryV1()
    })

    onSucceedSubmitProjectPlanCommandV1(() =>
    {
        runManageProjectPlansPageQueryV1()
    })

    onSucceedUnsubmitProjectPlanCommandV1(() =>
    {
        runManageProjectPlansPageQueryV1()
    })

    onSucceedUpdateProjectActivePlanCommandV1(() =>
    {
        runManageProjectPlansPageQueryV1()
    })

    onSucceedExportProjectPlanCommandV1(() =>
    {
        if (resultExportProjectPlanCommandV1.value)
        {
            location.href = resultExportProjectPlanCommandV1.value
        }
    })

    onSucceedCreateEnterpriseTemplateFromProjectPlanCommandV1(() =>
    {
        alert("Done");
        runManageProjectPlansPageQueryV1()
    })

    onSucceedCreatePlanFromTemplateCommandV1(() =>
    {
        runManageProjectPlansPageQueryV1()
    })


</script>

<template>
    <div>
        <ProjectPlanEditor :viewModel="projectPlanEditor" @form-submit="handleProjectPlanEditorFormSubmit" />

        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else-if="isProjectOwner" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">

            <div class="row">
                <div class="col-lg-6 d-flex align-items-end">
                    <h1 class="project-title">
                        <a class="project-title-link"
                           v-sanitizedHref="`/#/portfolio/alternative/dashboard/${workspaceId}/${tchProjectId}/${projectId}`">

                            {{ projectName }}

                            <i class="fa fa-external-link-alt project-title-icon"></i>
                        </a>
                    </h1>

                </div>
                <div class="col-lg-6 text-right align-self-end">

                    <div class="dropdown d-inline">
                        <button class="btn btn-outline-secondary mr-1" type="button" data-toggle="dropdown" style="padding: 0.7em 2em;">
                            Create a business case
                        </button>
                        <div class="dropdown-menu dropdown-menu-right" style="max-height: 300px; overflow-y: auto;">
                            <h6 class="dropdown-header">Business case templates</h6>
                            <small v-if="!planTemplates.length" class="text-muted px-4 mb-1 font-italic d-block">
                                You don't have any templates yet.
                            </small>
                            <a class="dropdown-item" v-for="t in planTemplates" :key="t.Id" @click="handleCreatePlanFromTemplateCommand(t)">
                                {{t.Name}}
                            </a>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" @click="showPlanCreator()">Custom business case</a>
                        </div>
                    </div>

                </div>
            </div>
            <hr />

            <div v-if="rm">
                <div v-if="rm.Plans.length === 0">
                    <div class="dropdown d-inline">
                        <button class="btn btn-success" type="button" data-toggle="dropdown" style="padding: 0.7em 2em;">
                            Create a business case
                        </button>
                        <div class="dropdown-menu dropdown-menu-right" style="max-height: 300px; overflow-y: auto;">
                            <h6 class="dropdown-header">Business case templates</h6>
                            <small v-if="!planTemplates.length" class="text-muted px-4 mb-1 font-italic d-block">
                                You don't have any templates yet.
                            </small>
                            <a class="dropdown-item" v-for="t in planTemplates" :key="t.Id" @click="showPlanCreator(t)">
                                {{t.Name}}
                            </a>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" @click="showPlanCreator()">Custom business case</a>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <table class="table table-bordered table-hover">
                        <thead>
                            <tr class="table-secondary">
                                <th>Active business case</th>
                                <th style="width: 150px;" class="text-center">Created</th>
                                <th style="width: 150px;" class="text-center">Modified</th>
                                <th style="width: 150px;" class="text-center">Activated</th>
                                <th style="width: 150px;" class="text-center">Submitted</th>
                                <th style="width: 150px;" class="text-center">Submission due</th>
                                <th class="text-center">Duration (months)</th>
                                <th class="text-center">Granularity</th>
                                <th class="text-center">Denomination</th>
                                <th style="width: 120px;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="activePlan">
                                <td>
                                    <a class="text-primary" v-sanitizedHref="`/#/q/project/revenues/${workspaceId}/${tchProjectId}/${projectId}/${activePlan.Id}`">{{activePlan.Name}}</a>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(activePlan.CreatedDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(activePlan.CreatedDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(activePlan.ModifiedDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(activePlan.ModifiedDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(activePlan.ActivationDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(activePlan.ActivationDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(activePlan.SubmissionDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(activePlan.SubmissionDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(activePlan.SubmissionDueDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(activePlan.SubmissionDueDate)}}</small>
                                </td>

                                <td class="text-center">{{activePlan.Duration}}</td>
                                <td class="text-center">{{activePlan.Granularity}}</td>
                                <td class="text-center">{{activePlan.Denomination}}</td>
                                
                                <td>
                                    <div class="btn-group">
                                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Actions</button>
                                        <div class="dropdown-menu dropdown-menu-right">

                                            <!-- Settings -->
                                            <button class="dropdown-item"
                                                    :disabled="activePlan.IsSubmitted"
                                                    @click="showPlanEditor(activePlan)">
                                                Settings
                                            </button>

                                            <div class="dropdown-divider"></div>

                                            <!-- Submit (only if not yet submitted) -->
                                            <button v-if="!activePlan.IsSubmitted"
                                                    class="dropdown-item"
                                                    @click="handleSubmitProjectPlanCommand(activePlan)">
                                                Submit
                                            </button>

                                            <!-- Unsubmit (only if submitted and user is a workspace member) -->
                                            <button v-if="activePlan.IsSubmitted && isWorkspaceMember"
                                                    class="dropdown-item"
                                                    @click="handleUnsubmitProjectPlanCommand(activePlan)">
                                                Unsubmit
                                            </button>

                                            <!-- Other actions -->
                                            <button class="dropdown-item"
                                                    @click="handleCloneProjectPlanCommand(activePlan)">
                                                Clone
                                            </button>
                                            <a class="dropdown-item"
                                               @click="handleExportProjectPlanCommand(activePlan)">
                                                Download as xls
                                            </a>

                                            <button class="dropdown-item" title="Available to enterprise admin"
                                                    :disabled="!isStrategyAdmin"
                                                    @click="handleCreateEnterpriseTemplateFromProjectPlanCommand(activePlan)">
                                                Save as template
                                            </button>

                                            <div class="dropdown-divider"></div>

                                            <button class="dropdown-item" disabled>
                                                Delete
                                            </button>

                                        </div>
                                    </div>

                                </td>
                            </tr>

                            <tr class="table-secondary" v-if="backupPlans.length > 0">
                                <td colspan="10">Backup business cases</td>
                            </tr>
                            <tr v-for="plan in backupPlans" :key="plan.Id">
                                <td>
                                    <a class="text-primary" v-sanitizedHref="`/#/q/project/revenues/${workspaceId}/${tchProjectId}/${projectId}/${plan.Id}`">{{plan.Name}}</a>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(plan.CreatedDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(plan.CreatedDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(plan.ModifiedDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(plan.ModifiedDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(plan.ActivationDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(plan.ActivationDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(plan.SubmissionDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(plan.SubmissionDate)}}</small>
                                </td>
                                <td class="text-center">
                                    <span>{{getFormattedDateStr(plan.SubmissionDueDate)}}</span><br />
                                    <small>{{getFormattedTimeStr(plan.SubmissionDueDate)}}</small>
                                </td>
                                <td class="text-center">{{plan.Duration}}</td>
                                <td class="text-center">{{plan.Granularity}}</td>
                                <td class="text-center">{{plan.Denomination}}</td>

                                <td>
                                    <div class="btn-group">
                                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Actions</button>
                                        <div class="dropdown-menu dropdown-menu-right">

                                            <!-- Settings -->
                                            <button class="dropdown-item"
                                                    :disabled="plan.IsSubmitted"
                                                    @click="showPlanEditor(plan)">
                                                Settings
                                            </button>

                                            <div class="dropdown-divider"></div>

                                            <!-- Submit (only if not yet submitted) -->
                                            <button v-if="!plan.IsSubmitted"
                                                    class="dropdown-item"
                                                    @click="handleSubmitProjectPlanCommand(plan)">
                                                Submit
                                            </button>

                                            <!-- Unsubmit (only if submitted and user is a workspace member) -->
                                            <button v-if="plan.IsSubmitted && isWorkspaceMember"
                                                    class="dropdown-item"
                                                    @click="handleUnsubmitProjectPlanCommand(plan)">
                                                Unsubmit
                                            </button>

                                            <!-- Other actions -->
                                            <button class="dropdown-item" @click="handleCloneProjectPlanCommand(plan)">
                                                Clone
                                            </button>
                                            <button class="dropdown-item" @click="handleUpdateProjectActivePlanCommand(plan)">
                                                Set as Active
                                            </button>
                                            <a class="dropdown-item" @click="handleExportProjectPlanCommand(plan)">
                                                Download as xls
                                            </a>

                                            <div class="dropdown-divider"></div>

                                            <button class="dropdown-item" 
                                                    :disabled="plan.IsSubmitted"
                                                    @click="handleDeleteProjectPlanCommand(plan)">
                                                Delete
                                            </button>

                                        </div>
                                    </div>

                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    /* ===== Project title ===== */

    .project-title {
        font-size: 1.4em;
        font-weight: 400;
        color: #33475b;
        margin: 0 0 4px 0;
        letter-spacing: 0.01em;
    }

    .project-title-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: inherit;
        text-decoration: none;
        cursor: pointer;
    }

        .project-title-link:hover {
            color: #2f6fdd;
        }

    .project-title-icon {
        font-size: 0.7em;
        opacity: 0;
        margin-top: 2px;
        transition: opacity 0.15s ease;
    }

    .project-title-link:hover .project-title-icon {
        opacity: 0.6;
    }
</style>
